// Pure inspection (check-in / check-out) domain constants.
// This folder is shared byte for byte between GOZY-API and GOZY-WEB: no Sequelize, no GraphQL, no config access.

export const INSPECTION_TYPES = Object.freeze({ checkin: 'checkin', checkout: 'checkout' });

export const INSPECTION_STATUS = Object.freeze({ draft: 'draft', to_validate: 'to_validate', validated: 'validated' });

// Ordered from the emptiest to the fullest tank: the index is used to compute the fuel delta.
export const FUEL_LEVELS = Object.freeze(['reserve', 'quarter', 'half', 'three_quarters', 'full']);

export const GENERAL_STATES = Object.freeze(['clean', 'average', 'dirty']);

// vehicle: check-in reference photos, damage: damage photos of a check-out, attachment: dispute supporting documents.
export const PHOTO_KINDS = Object.freeze(['vehicle', 'damage', 'attachment']);

export const ACTOR_ROLES = Object.freeze(['host', 'guest', 'admin', 'system']);

export const LIMITS = Object.freeze({
  photoMax: 8,
  commentMax: 500,
  descriptionMax: 500,
  motiveMax: 500,
  statementMax: 2000,
  mileageMax: 9999999,
  attachmentMax: 8,
  // Non blocking alert threshold (D14).
  dailyMileageAlert: 1000
});

export const DISPUTE_STATUS = Object.freeze({ open: 'open', in_review: 'in_review', resolved: 'resolved', closed: 'closed' });

export const ACTIVE_DISPUTE_STATUSES = Object.freeze(['open', 'in_review']);

export const DECISION_TYPES = Object.freeze(['release_all', 'retain_part', 'retain_all', 'no_action', 'request_indemnity']);

export const REASON_CODES = Object.freeze(['damage', 'responsibility', 'settlement', 'other']);

export const CLOSURE_STATES = Object.freeze(['open', 'closed', 'dispute_closed']);

export const GLOBAL_STATUS = Object.freeze({
  upcoming: 'upcoming',
  in_progress: 'in_progress',
  to_validate: 'to_validate',
  damage_declared: 'damage_declared',
  dispute: 'dispute',
  completed: 'completed',
  dispute_closed: 'dispute_closed',
  cancelled: 'cancelled'
});

// Platform settings (SiteSettings rows, edited by the admin). Defaults follow decisions D1, D2, D7, D11, D13, D18.
export const DEFAULT_SETTINGS = Object.freeze({
  inspectionEnabled: true,
  checkinOpensHoursBefore: 0,
  checkoutWindowDays: 7,
  inspectionGraceHours: 48,
  photoMax: 8,
  photoMin: 0,
  disputeWindowDays: 7,
  timeZone: 'UTC'
});

export const SETTING_NAMES = Object.freeze([
  'inspectionEnabled',
  'checkinOpensHoursBefore',
  'checkoutWindowDays',
  'inspectionGraceHours',
  'photoMax',
  'photoMin',
  'disputeWindowDays',
  'inspectionTimeZone'
]);

const toInteger = (value, fallback, min, max) => {
  if (value === null || value === undefined || value === '') return fallback;
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(number)));
};

const toBoolean = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  const text = String(value).trim().toLowerCase();
  if (['false', '0', 'no', 'off'].includes(text)) return false;
  if (['true', '1', 'yes', 'on'].includes(text)) return true;
  return fallback;
};

const isValidTimeZone = (timeZone) => {
  if (!timeZone || typeof timeZone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch (error) {
    return false;
  }
};

// Turns raw SiteSettings values (strings) into a safe settings object.
export function normalizeInspectionSettings(raw) {
  const source = raw || {};
  const photoMax = toInteger(source.photoMax, DEFAULT_SETTINGS.photoMax, 1, LIMITS.photoMax);
  return {
    inspectionEnabled: toBoolean(source.inspectionEnabled, DEFAULT_SETTINGS.inspectionEnabled),
    checkinOpensHoursBefore: toInteger(source.checkinOpensHoursBefore, DEFAULT_SETTINGS.checkinOpensHoursBefore, 0, 24 * 30),
    checkoutWindowDays: toInteger(source.checkoutWindowDays, DEFAULT_SETTINGS.checkoutWindowDays, 0, 90),
    inspectionGraceHours: toInteger(source.inspectionGraceHours, DEFAULT_SETTINGS.inspectionGraceHours, 0, 24 * 30),
    photoMax,
    photoMin: Math.min(photoMax, toInteger(source.photoMin, DEFAULT_SETTINGS.photoMin, 0, LIMITS.photoMax)),
    disputeWindowDays: toInteger(source.disputeWindowDays, DEFAULT_SETTINGS.disputeWindowDays, 0, 365),
    timeZone: isValidTimeZone(source.inspectionTimeZone || source.timeZone) ? (source.inspectionTimeZone || source.timeZone) : DEFAULT_SETTINGS.timeZone
  };
}
